import { Navigate } from 'react-router-dom';
import { useUserControlsContext, useUserControlsLoaded } from './contexts/UserControlsContext';
import { hasRequiredRole } from './lib/roles';

/**
 * Route wrapper that role-gates admin surfaces. Mirror of RequireSignedOut —
 * same three-state shape, gated on role slugs instead of Clerk auth state.
 *
 * Three-state contract:
 * - Not loaded (`useUserControlsLoaded()` false) → render nothing. The
 *   `/api/me` query hasn't settled yet; rendering a redirect here would flash
 *   a bounce for a legitimate admin whose roles just haven't arrived, and
 *   rendering children would briefly expose a gated surface to someone who
 *   turns out to lack the role.
 * - Loaded and the signed-in user's roles don't satisfy `anyOf` → redirect to
 *   `/` (`<Navigate replace>`). `'admin'` is the universal wildcard — holding
 *   it satisfies any `anyOf`, including an empty one. An empty `anyOf` with no
 *   `'admin'` role means admin-only: no narrower slug can pass.
 * - Loaded and satisfied → render children.
 */
export default function RequireRole({
  anyOf,
  children,
}: {
  anyOf: string[];
  children: React.ReactNode;
}) {
  const { roles } = useUserControlsContext();
  const isLoaded = useUserControlsLoaded();

  if (!isLoaded) {
    return null;
  }

  if (!hasRequiredRole(roles, anyOf)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
